/**
 * Badge Component
 *
 * Small status indicators, tags, and labels.
 * Used for campaign status, platform indicators, counts, etc.
 *
 * Features:
 * - 7 semantic variants
 * - 3 sizes
 * - Optional colored dot indicator
 * - Removable with X button
 * - Pill or rounded shape
 * - Full dark mode support
 *
 * Usage:
 *   <Badge variant="success" dot>Active</Badge>
 *   <Badge variant="primary" removable onRemove={() => removeTag(id)}>
 *     React
 *   </Badge>
 */

import React from 'react';
import { cn } from '@/lib/utils';

export interface BadgeProps {
  /** Visual variant of the badge */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' | 'outline' | 'blue' | 'green' | 'red' | 'yellow' | 'purple' | 'gray';
  /** Size of the badge */
  size?: 'sm' | 'md' | 'lg';
  /** Use pill shape (fully rounded) */
  rounded?: boolean;
  /** Show colored dot before text */
  dot?: boolean;
  /** Show remove button */
  removable?: boolean;
  /** Callback when remove button is clicked */
  onRemove?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Badge content */
  children: React.ReactNode;
}

export const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  rounded = true,
  dot = false,
  removable = false,
  onRemove,
  className,
  children
}) => {
  // Theme-board status chips: soft fill, strong text of the same colour; always with a word.
  const variants = {
    default: 'bg-inset text-navy-ink',
    primary: 'bg-brand-soft text-brand-ink',
    success: 'bg-ok-soft text-ok',
    warning: 'bg-warn-soft text-warn',
    error: 'bg-bad-soft text-bad',
    info: 'bg-work-soft text-work',
    outline: 'bg-transparent shadow-ring-line text-ink-2',
    // Color variants (aliases)
    blue: 'bg-work-soft text-work',
    green: 'bg-ok-soft text-ok',
    red: 'bg-bad-soft text-bad',
    yellow: 'bg-warn-soft text-warn',
    purple: 'bg-brand-soft text-brand-ink',
    gray: 'bg-inset text-ink-2'
  };

  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const dotColors = {
    default: 'bg-ink-3', primary: 'bg-brand', success: 'bg-ok', warning: 'bg-warn', error: 'bg-bad', info: 'bg-work-dot',
    outline: 'bg-ink-3', blue: 'bg-work-dot', green: 'bg-ok', red: 'bg-bad', yellow: 'bg-warn', purple: 'bg-brand', gray: 'bg-ink-3'
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 font-medium',
        rounded ? 'rounded-full' : 'rounded-md',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {/* Colored dot indicator */}
      {dot && (
        <span className={cn('w-1.5 h-1.5 rounded-full', dotColors[variant])} />
      )}

      {/* Badge text */}
      {children}

      {/* Remove button */}
      {removable && onRemove && (
        <button
          onClick={onRemove}
          className="ml-1 hover:opacity-70 transition-opacity focus:outline-none"
          aria-label="Remove"
          type="button"
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
          </svg>
        </button>
      )}
    </span>
  );
};

export default Badge;
